import { Suspense, lazy, useEffect, type ReactNode } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { VisitorTracker } from "@/components/VisitorTracker";
import { bootstrapAuth } from "@/lib/api";
import { registrationEnabled } from "@/config/features";

// Home is the landing page so we keep it in the main bundle for fast first paint.
import HomePage from "@/pages/HomePage";
import NotFoundPage from "@/pages/NotFoundPage";

// Everything else is lazy-loaded so the initial bundle stays small —
// auth, dashboard, sponsorship and race-day pages only load when visited.
const SponsorshipPage = lazy(() => import("@/pages/SponsorshipPage"));
const SponsorsPage = lazy(() => import("@/pages/SponsorsPage"));
const RaceDayPage = lazy(() => import("@/pages/RaceDayPage"));
const RegistrationPage = lazy(() => import("@/pages/RegistrationPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const AccessibilityPage = lazy(() => import("@/pages/AccessibilityPage"));

const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const SignupPage = lazy(() => import("@/pages/auth/SignupPage"));
const VerifyEmailPage = lazy(() => import("@/pages/auth/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage"));

const DashboardLayout = lazy(() =>
  import("@/pages/dashboard/DashboardLayout").then((m) => ({ default: m.DashboardLayout }))
);
const OverviewPage = lazy(() => import("@/pages/dashboard/OverviewPage"));
const CommitmentPage = lazy(() => import("@/pages/dashboard/CommitmentPage"));
const RunnersPage = lazy(() => import("@/pages/dashboard/RunnersPage"));
const PwdRegistrationPage = lazy(() => import("@/pages/dashboard/PwdRegistrationPage"));
const PwdRunnersPage = lazy(() => import("@/pages/dashboard/PwdRunnersPage"));

function RouteFallback() {
  return (
    <div className="container py-32 flex items-center justify-center text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}

// Registration/login/dashboard are gated off for the marketing-only launch (no
// backend). In that build any direct navigation to those routes redirects Home so
// no broken, backend-dependent page is ever reachable.
function gated(element: ReactNode): ReactNode {
  return registrationEnabled ? element : <Navigate to="/" replace />;
}

export default function App() {
  // Try to restore the session via the httpOnly refresh cookie on mount. Skipped
  // when registration is gated off so the marketing build makes no backend call.
  useEffect(() => {
    if (registrationEnabled) void bootstrapAuth();
  }, []);

  // The prerendered SPA fallback (dist/index.html = Home snapshot) hides #root
  // via html[data-prerender-mismatch] when served for a non-"/" deep link. Once
  // React has mounted the real route, clear the guard so the page is visible.
  useEffect(() => {
    document.documentElement.removeAttribute("data-prerender-mismatch");
  }, []);

  return (
    <ErrorBoundary>
      {/* Anonymous page-view tracking — fires on every route change. */}
      <VisitorTracker />
      <Routes>
        <Route element={<PublicLayout />}>
          {/* Home stays eager — best first-paint */}
          <Route index element={<HomePage />} />

          {/* Public marketing pages — lazy */}
          <Route
            path="/sponsorship"
            element={<Suspense fallback={<RouteFallback />}><SponsorshipPage /></Suspense>}
          />
          {/* /sponsorship = how to become one; /sponsors = who they are. */}
          <Route
            path="/sponsors"
            element={<Suspense fallback={<RouteFallback />}><SponsorsPage /></Suspense>}
          />
          <Route
            path="/race-details"
            element={<Suspense fallback={<RouteFallback />}><RaceDayPage /></Suspense>}
          />
          {/* Backward-compat: the page URL was renamed /race-day → /race-details.
              Redirect old links (bookmarks, prior sitemap/prerender, CDN SPA fallback). */}
          <Route path="/race-day" element={<Navigate to="/race-details/" replace />} />
          <Route
            path="/registration"
            element={gated(<Suspense fallback={<RouteFallback />}><RegistrationPage /></Suspense>)}
          />
          <Route
            path="/contact"
            element={<Suspense fallback={<RouteFallback />}><ContactPage /></Suspense>}
          />
          <Route
            path="/accessibility"
            element={<Suspense fallback={<RouteFallback />}><AccessibilityPage /></Suspense>}
          />

          {/* Auth */}
          <Route path="/login" element={gated(<Suspense fallback={<RouteFallback />}><LoginPage /></Suspense>)} />
          <Route path="/signup" element={gated(<Suspense fallback={<RouteFallback />}><SignupPage /></Suspense>)} />
          <Route path="/verify-email" element={gated(<Suspense fallback={<RouteFallback />}><VerifyEmailPage /></Suspense>)} />
          <Route
            path="/forgot-password"
            element={gated(<Suspense fallback={<RouteFallback />}><ForgotPasswordPage /></Suspense>)}
          />
          <Route
            path="/reset-password"
            element={gated(<Suspense fallback={<RouteFallback />}><ResetPasswordPage /></Suspense>)}
          />

          {/* Auth-gated dashboard */}
          <Route
            path="/dashboard"
            element={gated(
              <ProtectedRoute requireVerified>
                <Suspense fallback={<RouteFallback />}><DashboardLayout /></Suspense>
              </ProtectedRoute>
            )}
          >
            <Route index element={<Suspense fallback={<RouteFallback />}><OverviewPage /></Suspense>} />
            <Route path="commitment" element={<Suspense fallback={<RouteFallback />}><CommitmentPage /></Suspense>} />
            <Route path="runners" element={<Suspense fallback={<RouteFallback />}><RunnersPage /></Suspense>} />
            <Route path="pwd" element={<Suspense fallback={<RouteFallback />}><PwdRegistrationPage /></Suspense>} />
            <Route path="pwd-runners" element={<Suspense fallback={<RouteFallback />}><PwdRunnersPage /></Suspense>} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </ErrorBoundary>
  );
}
